<script setup lang="ts">
// DESIGN.md section 9.1 — Players (round-4 feedback item 1). Merges the old standalone
// Rankings screen into this one: the Players page IS the ranking (rank #, avatar, name, elo,
// W-L, win %), now with an inline search field. Search reuses the SAME endpoint
// PlayerSearchList's "pick a name" dialog already calls (`GET /api/players?q=`) rather than a
// bespoke one — it can surface ANY player (incl. unranked guests with 0 matches), which the
// ranked list (`GET /api/rankings`, matches > 0 only) never would. The old `#rankings` hash
// redirects here (store/nav.ts's parseHash) so a stale bookmark/back-button never lands on a
// vanished page.
import { computed, ref, watch } from 'vue'
import Avatar from '../components/Avatar.vue'
import OnScreenKeyboard from '../components/OnScreenKeyboard.vue'
import { getPlayers, getRankings } from '../backend'
import { navigate } from '../store/nav'
import type { Player, RankingEntry, RatingPool } from '../types'

const pool = ref<RatingPool>('rapid')
const rankingEntries = ref<RankingEntry[]>([])
const loadingRankings = ref(false)
const loadError = ref(false)

const query = ref('')
const searchResults = ref<Player[]>([])
const searching = ref(false)
const inputEl = ref<HTMLInputElement | null>(null)
const keyboardOpen = ref(false)

async function loadRankings() {
  loadingRankings.value = true
  loadError.value = false
  try {
    const res = await getRankings(pool.value)
    rankingEntries.value = res.entries
  } catch {
    loadError.value = true
  } finally {
    loadingRankings.value = false
  }
}

watch(pool, loadRankings, { immediate: true })

const isSearching = computed(() => query.value.trim().length > 0)

let debounceTimer: ReturnType<typeof setTimeout> | null = null
async function runSearch() {
  searching.value = true
  try {
    const res = await getPlayers(query.value, 50)
    searchResults.value = res.items
  } finally {
    searching.value = false
  }
}
watch(query, () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  if (!isSearching.value) {
    searchResults.value = []
    return
  }
  debounceTimer = setTimeout(runSearch, 150)
})

interface DisplayRow {
  rank: number | null
  player: Player
  rating: number
  provisional: boolean
  wins: number
  losses: number
  winRate: number | null
}

// While searching, results come from the plain player-search endpoint (no rank/win-rate) — map
// them into the same row shape as a ranking entry, sourcing rating/wins/losses from the
// selected pool and sorting by rating desc so the list still reads as "ranked by elo" even for
// a filtered search.
const displayRows = computed<DisplayRow[]>(() => {
  if (isSearching.value) {
    return searchResults.value
      .map((p) => {
        const r = p.ratings[pool.value]
        return {
          rank: null,
          player: p,
          rating: r.rating,
          provisional: r.provisional,
          wins: r.wins,
          losses: r.losses,
          winRate: r.matches > 0 ? r.wins / r.matches : null,
        }
      })
      .sort((a, b) => b.rating - a.rating)
  }
  return rankingEntries.value.map((e) => ({
    rank: e.rank,
    player: e.player,
    rating: e.rating,
    provisional: e.provisional,
    wins: e.wins,
    losses: e.losses,
    winRate: e.winRate,
  }))
})

const isBusy = computed(() => (isSearching.value ? searching.value : loadingRankings.value))

function openPlayer(id: number) {
  navigate('player', { playerId: id })
}

function onKeyboardDone() {
  inputEl.value?.blur()
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-3">
    <div class="flex items-center gap-3">
      <h1 class="text-xl font-bold flex-1">Players</h1>
      <button type="button" class="tap-target px-4 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold" @click="navigate('versus')">
        Versus
      </button>
    </div>

    <div class="flex gap-2">
      <button
        type="button"
        class="tap-target px-5 rounded-xl font-semibold"
        :class="pool === 'rapid' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
        @click="pool = 'rapid'"
      >
        Rapid
      </button>
      <button
        type="button"
        class="tap-target px-5 rounded-xl font-semibold"
        :class="pool === 'classical' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
        @click="pool = 'classical'"
      >
        Classic
      </button>
    </div>

    <input
      ref="inputEl"
      v-model="query"
      type="text"
      placeholder="Search a name…"
      class="tap-target rounded-xl bg-neutral-900 border border-neutral-800 px-4 text-base outline-none focus:ring-2 focus:ring-emerald-500"
      @focus="keyboardOpen = true"
      @blur="keyboardOpen = false"
    />

    <div v-if="loadError && !isSearching" class="flex-1 flex items-center justify-center text-neutral-500">Unavailable</div>
    <div v-else class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1" :style="keyboardOpen ? { paddingBottom: '260px' } : undefined">
      <div v-if="!isBusy && displayRows.length === 0" class="text-neutral-500">
        {{ isSearching ? 'No players found' : 'No ranked players yet' }}
      </div>
      <button
        v-for="row in displayRows"
        :key="row.player.id"
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-900 border border-neutral-800 px-3 py-2 text-left active:bg-neutral-800"
        @click="openPlayer(row.player.id)"
      >
        <span class="w-8 text-right font-mono text-neutral-500">{{ row.rank ?? '—' }}</span>
        <Avatar :player="row.player" :size="40" />
        <span class="flex-1 font-semibold truncate">
          {{ row.player.displayName }}
          <span v-if="row.player.isGuest" class="ml-1 text-xs font-normal text-neutral-500">guest</span>
        </span>
        <span class="font-mono font-bold w-16 text-right">{{ row.rating }}<span v-if="row.provisional" class="text-neutral-500">?</span></span>
        <span class="font-mono text-neutral-400 w-20 text-right">{{ row.wins }}–{{ row.losses }}</span>
        <span class="font-mono text-neutral-400 w-16 text-right">{{ row.winRate != null ? `${Math.round(row.winRate * 100)}%` : '—' }}</span>
      </button>
    </div>

    <OnScreenKeyboard v-if="keyboardOpen" v-model="query" @done="onKeyboardDone" />
  </div>
</template>
